import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { documentService } from '../../services/documentService';
import { documentWorkflowService, DocumentWorkflowDto } from '../../services/documentWorkflowService';
import { documentSignatureService } from '../../services/documentSignatureService';
import { Document, DocumentDto, DocumentVersion } from '../../types/documents';
import { useToast } from '../../components/UI/ToastProvider';
import { useDocumentPermissions } from '../../hooks/useDocumentPermissions';
import { getWorkflowStatusBadgeClasses, getWorkflowStatusLabel } from '../../utils/workflowStatusBadge';
import {
  isCompletedSignatureRequestStatus,
  isInProgressSignatureRequestStatus,
} from '../../utils/signatureStatusUtils';

type Rec = Record<string, unknown>;
type LineDiffType = 'equal' | 'insert' | 'delete' | 'changed';
type DiffViewMode = 'visual' | 'line';

interface ParsedAttachment { name: string; url?: string; }
interface ParsedVersionPayload {
  title: string;
  status: string;
  updatedAt: string;
  coverImage: string;
  description: string;
  content: string;
  attachments: ParsedAttachment[];
  metadata: Rec;
}

interface LineDiffRow {
  leftNumber: number | null;
  rightNumber: number | null;
  leftText: string;
  rightText: string;
  type: LineDiffType;
}

const readOnlyModules = { toolbar: false };
const editModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image', 'video'],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    ['clean']
  ]
};
const editFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'image',
  'video',
  'color',
  'background',
  'font'
];
const isRecord = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const isHtml = (v: string) => (v || '').trim().startsWith('<') && (v || '').includes('>');
const first = (...vals: unknown[]) => vals.find(v => typeof v === 'string' && v.trim()) as string || '';
const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const norm = (v: string) => (v || '').replace(/\s+/g, ' ').trim();

const isLikelyImageUrl = (s: string) => {
  const t = (s || '').trim();
  if (!t || t === 'Não informado') return false;
  if (t.startsWith('<')) return false;
  const lower = t.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
  if (lower.startsWith('blob:') || lower.startsWith('data:image')) return true;
  return false;
};

const statusLabel = (status: string) => getWorkflowStatusLabel(status);

const statusClassName = (status: string) => getWorkflowStatusBadgeClasses(status);

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
};

const parseJson = (value: string): unknown | null => {
  const trimmed = (value || '').trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return null;
  try { return JSON.parse(trimmed); } catch { return null; }
};

const extractContent = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (!isRecord(v)) return '';
  return first(v.content, v.fileContent, v.text, v.html, v.value, v.url, v.storagePath);
};

const extractAttachments = (v: unknown): ParsedAttachment[] => {
  if (!Array.isArray(v)) return [];
  return v.map(item => {
    if (typeof item === 'string') return item.trim() ? { name: item.trim() } : null;
    if (!isRecord(item)) return null;
    const name = first(item.name, item.fileName, item.title, item.storagePath, item.url) || 'Arquivo sem nome';
    const url = first(item.url, item.storagePath, item.downloadUrl, item.fileUrl);
    return { name, url: url || undefined };
  }).filter((x): x is ParsedAttachment => Boolean(x));
};

const parsePayload = (payload: string): ParsedVersionPayload => {
  const fallback: ParsedVersionPayload = {
    title: '', status: '', updatedAt: '', coverImage: '', description: '',
    content: payload || '', attachments: [], metadata: {}
  };
  const parsed = parseJson(payload);
  if (!isRecord(parsed)) return fallback;

  const doc = isRecord(parsed.document) ? parsed.document : {};
  const meta = isRecord(parsed.metadata) ? parsed.metadata : {};
  const docMeta = isRecord(doc.metadata) ? doc.metadata : {};

  return {
    title: first(parsed.title, doc.title, meta.title, docMeta.title),
    status: first(parsed.status, doc.status, meta.status, docMeta.status),
    updatedAt: first(parsed.updatedAt, parsed.lastModifiedAt, doc.updatedAt, meta.updatedAt, meta.lastModifiedAt, docMeta.updatedAt),
    coverImage: first(parsed.coverImage, extractContent(parsed.cover), doc.coverImage, extractContent(doc.cover), meta.coverImage, docMeta.coverImage, meta.coverUrl),
    description: first(parsed.description, doc.description, meta.description, docMeta.description, meta.resume),
    content: first(extractContent(parsed.content), extractContent(parsed.payload), extractContent(doc.content), parsed.body, parsed.text, payload),
    attachments: [...extractAttachments(parsed.attachments), ...extractAttachments(doc.attachments), ...extractAttachments(meta.attachments)],
    metadata: { ...docMeta, ...meta }
  };
};

const parsePayloadWithFallback = (
  payload: string,
  fallback: { title?: string; status?: string; updatedAt?: string; description?: string } = {}
): ParsedVersionPayload => {
  const parsed = parsePayload(payload);
  return {
    ...parsed,
    title: parsed.title || fallback.title || '',
    status: parsed.status || fallback.status || '',
    updatedAt: parsed.updatedAt || fallback.updatedAt || '',
    description: parsed.description || fallback.description || ''
  };
};

const htmlToPlainTextPreserveLines = (html: string) => {
  if (!html) return '';
  if (!isHtml(html)) return String(html);
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const blockTags = new Set([
      'P', 'DIV', 'LI',
      'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'TR',
      'UL', 'OL',
      'BLOCKQUOTE',
      'PRE'
    ]);

    let out = '';
    const append = (s: string) => { out += s; };
    const ensureNewline = () => {
      if (!out.endsWith('\n')) append('\n');
    };

    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.nodeValue ?? '';
        // Ignora nós de texto só com whitespace (quebra/indentação do HTML).
        if (!raw || !raw.replace(/\s+/g, '').length) return;
        append(raw.replace(/\s+/g, ' '));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as HTMLElement;
      const tag = el.tagName;
      if (tag === 'BR') {
        append('\n');
        return;
      }

      for (const child of Array.from(el.childNodes)) walk(child);
      if (blockTags.has(tag)) ensureNewline();
    };

    for (const child of Array.from(doc.body.childNodes)) walk(child);
    return out.replace(/\r\n/g, '\n');
  } catch {
    return String(html);
  }
};

const wrapHtml = (v: string) => (v.trim() ? `<div>${v.replace(/\n/g, '<br />')}</div>` : '<p><br></p>');
const toHtml = (content: string) => (isHtml(content) ? content : wrapHtml(esc(content || '')));
const normalizeEditorHtml = (content: string) => (content || '')
  .replace(/\r\n/g, '\n')
  .replace(/>\s+</g, '><')
  .trim();

interface DiffChunk { type: 'equal' | 'insert' | 'delete'; text: string; }
interface FormattedHtmlDiff {
  previousHtml: string;
  currentHtml: string;
  hasDiff: boolean;
  hasInsertions: boolean;
  hasDeletions: boolean;
  hasModifications: boolean;
}

const pushDiffChunk = (list: DiffChunk[], type: DiffChunk['type'], text: string) => {
  if (!text) return;
  const last = list[list.length - 1];
  if (last && last.type === type) {
    last.text += text;
  } else {
    list.push({ type, text });
  }
};

const wrapDiffChunk = (chunk: DiffChunk): string => {
  if (chunk.type === 'equal') return chunk.text;
  if (chunk.type === 'insert') return `<span class="diff-ins">${chunk.text}</span>`;
  return `<span class="diff-del">${chunk.text}</span>`;
};

const buildFormattedHtmlDiff = (previousContent: string, currentContent: string): FormattedHtmlDiff => {
  const previousHtml = toHtml(previousContent || '');
  const currentHtml = toHtml(currentContent || '');
  if (previousHtml === currentHtml) {
    return {
      previousHtml,
      currentHtml,
      hasDiff: false,
      hasInsertions: false,
      hasDeletions: false,
      hasModifications: false
    };
  }

  const tokenRegex = /(<[^>]+>|\s+|[^<\s]+)/g;
  const a = previousHtml.match(tokenRegex) || [];
  const b = currentHtml.match(tokenRegex) || [];
  const n = a.length;
  const m = b.length;

  if (!n && !m) {
    return {
      previousHtml: '<p><br></p>',
      currentHtml: '<p><br></p>',
      hasDiff: false,
      hasInsertions: false,
      hasDeletions: false,
      hasModifications: false
    };
  }

  if (!n) {
    return {
      previousHtml: '<p><br></p>',
      currentHtml,
      hasDiff: true,
      hasInsertions: true,
      hasDeletions: false,
      hasModifications: false
    };
  }

  if (!m) {
    return {
      previousHtml,
      currentHtml: '<p><br></p>',
      hasDiff: true,
      hasInsertions: false,
      hasDeletions: true,
      hasModifications: false
    };
  }

  if (n * m > 1_200_000) {
    return {
      previousHtml,
      currentHtml,
      hasDiff: true,
      hasInsertions: true,
      hasDeletions: true,
      hasModifications: true
    };
  }

  const cols = m + 1;
  const mat = new Uint32Array((n + 1) * (m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const idx = i * cols + j;
      mat[idx] = a[i] === b[j]
        ? mat[(i + 1) * cols + j + 1] + 1
        : Math.max(mat[(i + 1) * cols + j], mat[i * cols + j + 1]);
    }
  }

  const previousChunks: DiffChunk[] = [];
  const currentChunks: DiffChunk[] = [];
  let hasInsertions = false;
  let hasDeletions = false;
  let i = 0;
  let j = 0;

  while (i < n && j < m) {
    if (a[i] === b[j]) {
      pushDiffChunk(previousChunks, 'equal', a[i]);
      pushDiffChunk(currentChunks, 'equal', b[j]);
      i++;
      j++;
      continue;
    }
    const down = mat[(i + 1) * cols + j];
    const right = mat[i * cols + j + 1];
    if (down >= right) {
      hasDeletions = true;
      pushDiffChunk(previousChunks, 'delete', a[i]);
      i++;
    } else {
      hasInsertions = true;
      pushDiffChunk(currentChunks, 'insert', b[j]);
      j++;
    }
  }

  while (i < n) {
    hasDeletions = true;
    pushDiffChunk(previousChunks, 'delete', a[i]);
    i++;
  }

  while (j < m) {
    hasInsertions = true;
    pushDiffChunk(currentChunks, 'insert', b[j]);
    j++;
  }

  const previousOut = previousChunks.map(wrapDiffChunk).join('');
  const currentOut = currentChunks.map(wrapDiffChunk).join('');

  return {
    previousHtml: previousOut || '<p><br></p>',
    currentHtml: currentOut || '<p><br></p>',
    hasDiff: true,
    hasInsertions,
    hasDeletions,
    hasModifications: hasInsertions && hasDeletions
  };
};

const normalizeLines = (content: string): string[] => {
  const normalized = htmlToPlainTextPreserveLines(content || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\r\n/g, '\n');

  const lines = normalized.split('\n').map(l => l.replace(/\s+$/g, ''));

  // Colapsa múltiplas linhas vazias intermediárias causadas por marcação/formatting.
  const compact: string[] = [];
  for (const line of lines) {
    if (line === '' && compact.length && compact[compact.length - 1] === '') continue;
    compact.push(line);
  }

  // O Quill costuma introduzir parágrafos vazios no início; remover evita "deslocar" o lado B.
  while (compact.length > 1 && compact[0] === '') compact.shift();

  // Evita inflar a comparação com linhas vazias finais (muito comum em HTML/Quill)
  while (compact.length > 1 && compact[compact.length - 1] === '') compact.pop();

  return compact;
};

const buildLineDiffRows = (contentA: string, contentB: string) => {
  const linesA = normalizeLines(contentA);
  const linesB = normalizeLines(contentB);
  const n = linesA.length;
  const m = linesB.length;

  // Heurística: se não há nenhuma linha (não vazia) em comum, alinhar por índice evita
  // o comportamento "todas as remoções e depois todas as inserções", que empurra a Versão atual para baixo.
  const setA = new Set(linesA.filter(l => l.trim()));
  let commonNonEmpty = 0;
  for (const l of linesB) {
    if (!l.trim()) continue;
    if (setA.has(l)) { commonNonEmpty++; break; }
  }
  if (!commonNonEmpty) {
    const rows: LineDiffRow[] = [];
    const max = Math.max(n, m);
    for (let k = 0; k < max; k++) {
      const leftHas = k < n;
      const rightHas = k < m;
      const leftText = leftHas ? linesA[k] : '';
      const rightText = rightHas ? linesB[k] : '';
      const type: LineDiffType = leftHas && rightHas
        ? (leftText === rightText ? 'equal' : 'changed')
        : (leftHas ? 'delete' : 'insert');
      rows.push({
        leftNumber: leftHas ? (k + 1) : null,
        rightNumber: rightHas ? (k + 1) : null,
        leftText,
        rightText,
        type
      });
    }
    return { rows, hasDiff: rows.some(r => r.type !== 'equal') };
  }

  const cols = m + 1;
  const matrix = new Uint16Array((n + 1) * (m + 1));

  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const idx = i * cols + j;
      matrix[idx] = linesA[i] === linesB[j]
        ? (matrix[(i + 1) * cols + j + 1] + 1)
        : Math.max(matrix[(i + 1) * cols + j], matrix[i * cols + j + 1]);
    }
  }

  const rows: LineDiffRow[] = [];
  let i = 0;
  let j = 0;
  let lineA = 1;
  let lineB = 1;

  while (i < n && j < m) {
    if (linesA[i] === linesB[j]) {
      rows.push({
        leftNumber: lineA++,
        rightNumber: lineB++,
        leftText: linesA[i],
        rightText: linesB[j],
        type: 'equal'
      });
      i++;
      j++;
      continue;
    }

    const down = matrix[(i + 1) * cols + j];
    const right = matrix[i * cols + j + 1];
    if (down >= right) {
      rows.push({
        leftNumber: lineA++,
        rightNumber: null,
        leftText: linesA[i],
        rightText: '',
        type: 'delete'
      });
      i++;
    } else {
      rows.push({
        leftNumber: null,
        rightNumber: lineB++,
        leftText: '',
        rightText: linesB[j],
        type: 'insert'
      });
      j++;
    }
  }

  while (i < n) {
    rows.push({
      leftNumber: lineA++,
      rightNumber: null,
      leftText: linesA[i],
      rightText: '',
      type: 'delete'
    });
    i++;
  }

  while (j < m) {
    rows.push({
      leftNumber: null,
      rightNumber: lineB++,
      leftText: '',
      rightText: linesB[j],
      type: 'insert'
    });
    j++;
  }

  for (let idx = 0; idx < rows.length - 1; idx++) {
    if (rows[idx].type === 'delete' && rows[idx + 1].type === 'insert') {
      rows[idx].type = 'changed';
      rows[idx + 1].type = 'changed';
    }
  }

  return {
    rows,
    hasDiff: rows.some(r => r.type !== 'equal')
  };
};

const stable = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v).trim();
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (!isRecord(v)) return String(v);
  return `{${Object.keys(v).sort().map(k => `${k}:${stable(v[k])}`).join('|')}}`;
};

const formatUnknown = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return 'Não informado';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.length ? v.map(x => typeof x === 'string' ? x : stable(x)).join(', ') : '[]';
  try { return JSON.stringify(v, null, 2); } catch { return stable(v); }
};

const lineBlockClass = (type: LineDiffType, side: 'left' | 'right') => {
  if (type === 'equal') return 'bg-white';
  if (type === 'delete') return side === 'left' ? 'bg-red-50' : 'bg-red-25';
  if (type === 'insert') return side === 'right' ? 'bg-green-50' : 'bg-green-25';
  return side === 'left' ? 'bg-red-50' : 'bg-green-50';
};

/** Identifica o snapshot do payload no servidor (compatível com camelCase e PascalCase da API). */
const versionPayloadFingerprint = (v: DocumentVersion | null) => {
  if (!v) return '';
  const sha =
    v.payloadSha256 ??
    (v as DocumentVersion & { PayloadSha256?: string | null }).PayloadSha256 ??
    '';
  return `${v.id}-${sha}-${v.payload?.length ?? 0}`;
};

export const DocumentVersionComparePage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { permissions } = useDocumentPermissions(documentId || null);

  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentDescription, setDocumentDescription] = useState<string>('');
  const [documentCoverUrl, setDocumentCoverUrl] = useState<string | null>(null);
  const [coverModalUrl, setCoverModalUrl] = useState<string | null>(null);
  const [versionAId, setVersionAId] = useState<string | null>(null);
  const [versionBId, setVersionBId] = useState<string | null>(null);
  const [editedContentB, setEditedContentB] = useState<string>('');
  const [savingB, setSavingB] = useState(false);
  const [diffViewMode, setDiffViewMode] = useState<DiffViewMode>('visual');
  const [activeHunkIndex, setActiveHunkIndex] = useState(0);
  const lineDiffContainerRef = useRef<HTMLDivElement | null>(null);
  const visualLeftContainerRef = useRef<HTMLDivElement | null>(null);
  const visualRightContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeVisualChangeIndex, setActiveVisualChangeIndex] = useState(0);
  const [visualChangeBlocks, setVisualChangeBlocks] = useState<Array<{ leftTop: number; rightTop: number; leftEnd: number; rightEnd: number }>>([]);
  const [summaryOnlyChanged, setSummaryOnlyChanged] = useState(false);
  const [hasBlockingSignature, setHasBlockingSignature] = useState(false);
  const [documentWorkflow, setDocumentWorkflow] = useState<DocumentWorkflowDto | null>(null);

  const versionA = useMemo(
    () => versions.find(v => v.id === versionAId) || null,
    [versions, versionAId]
  );
  const versionB = useMemo(
    () => versions.find(v => v.id === versionBId) || null,
    [versions, versionBId]
  );

  const resolveCoverUrl = useCallback(
    (version: DocumentVersion | null, rawFromPayload: string): string | null => {
      const raw = (rawFromPayload || '').trim();
      if (raw && raw !== 'Não informado' && isLikelyImageUrl(raw)) return raw;
      if (version?.isCurrent && documentCoverUrl) return documentCoverUrl;
      return null;
    },
    [documentCoverUrl]
  );

  const visualLeftQuillKey = useMemo(
    () => `diff-vis-l-${versionPayloadFingerprint(versionA)}`,
    [versionA]
  );
  const visualRightQuillKey = useMemo(
    () => `diff-vis-r-${versionPayloadFingerprint(versionB)}`,
    [versionB]
  );

  const payloadA = useMemo(
    () => versionA
      ? parsePayloadWithFallback(versionA.payload, {
          title: documentTitle,
          status: versionA.status,
          updatedAt: versionA.createdAt,
          description: documentDescription || undefined
        })
      : null,
    [versionA, documentTitle, documentDescription]
  );
  const payloadB = useMemo(
    () => versionB
      ? parsePayloadWithFallback(versionB.payload, {
          title: documentTitle,
          status: versionB.status,
          updatedAt: versionB.createdAt,
          description: documentDescription || undefined
        })
      : null,
    [versionB, documentTitle, documentDescription]
  );

  const canEditDocumentLikeContent = useMemo(() => {
    if (!documentWorkflow)
    {
      return true;
    }

    const steps = documentWorkflow.steps ?? [];
    if (steps.length === 0)
    {
      return true;
    }

    const isClosed = (status: string | undefined) =>
    {
      const s = (status || '').toLowerCase();
      return s === 'completed' || s === 'rejected' || s === 'cancelled';
    };

    const sorted = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
    const open = sorted.find(s => !isClosed(s.status));
    if (!open)
    {
      return false;
    }

    const allowed = (open.definitionAllowedActions ?? [])
      .map(a => (typeof a === 'string' ? a.trim() : ''))
      .filter(Boolean);

    return allowed.some(a => a.toLowerCase() === 'save');
  }, [documentWorkflow]);

  const canEditB = Boolean(
    versionB?.isCurrent
    && documentId
    && permissions.canEdit
    && !hasBlockingSignature
    && canEditDocumentLikeContent
  );
  const baseContentB = useMemo(() => toHtml(payloadB?.content || ''), [payloadB]);
  const effectiveContentB = useMemo(() => {
    if (!payloadB) return '';
    if (canEditB) return editedContentB;
    return payloadB.content;
  }, [canEditB, editedContentB, payloadB]);
  const hasPendingChangesB = useMemo(
    () => canEditB && normalizeEditorHtml(editedContentB) !== normalizeEditorHtml(baseContentB),
    [baseContentB, canEditB, editedContentB]
  );

  const formattedDiff = useMemo(
    () => payloadA && payloadB
      ? buildFormattedHtmlDiff(payloadA.content, effectiveContentB)
      : { previousHtml: '<p><br></p>', currentHtml: '<p><br></p>', hasDiff: false, hasInsertions: false, hasDeletions: false, hasModifications: false },
    [payloadA, payloadB, effectiveContentB]
  );

  const summaryRows = useMemo(() => {
    if (!payloadA || !payloadB) return [];
    const coverA = resolveCoverUrl(versionA, payloadA.coverImage || '');
    const coverB = resolveCoverUrl(versionB, payloadB.coverImage || '');
    const att = (list: ParsedAttachment[]) => list.map(i => `${i.name}|${i.url || ''}`).join('||');
    return [
      { key: 'title', label: 'Título', changed: norm(payloadA.title) !== norm(payloadB.title), a: payloadA.title || 'Não informado', b: payloadB.title || 'Não informado' },
      { key: 'status', label: 'Status', changed: norm(payloadA.status) !== norm(payloadB.status), a: payloadA.status ? statusLabel(payloadA.status) : 'Não informado', b: payloadB.status ? statusLabel(payloadB.status) : 'Não informado' },
      { key: 'updatedAt', label: 'Atualizado em', changed: norm(payloadA.updatedAt) !== norm(payloadB.updatedAt), a: payloadA.updatedAt ? formatDateTime(payloadA.updatedAt) : 'Não informado', b: payloadB.updatedAt ? formatDateTime(payloadB.updatedAt) : 'Não informado' },
      { key: 'coverImage', label: 'Imagem de capa', changed: norm(coverA || '') !== norm(coverB || ''), a: coverA || '', b: coverB || '' },
      { key: 'attachments', label: 'Arquivos', changed: norm(att(payloadA.attachments)) !== norm(att(payloadB.attachments)), a: payloadA.attachments, b: payloadB.attachments },
      { key: 'description', label: 'Descrição', changed: norm(payloadA.description) !== norm(payloadB.description), a: payloadA.description || 'Não informado', b: payloadB.description || 'Não informado' }
    ] as const;
  }, [payloadA, payloadB, versionA, versionB, resolveCoverUrl]);

  const displayedSummaryRows = useMemo(
    () => (summaryOnlyChanged ? summaryRows.filter(r => r.changed) : summaryRows),
    [summaryRows, summaryOnlyChanged]
  );
  const summaryChangedCount = useMemo(() => summaryRows.filter(r => r.changed).length, [summaryRows]);

  const metadataRows = useMemo(() => {
    if (!payloadA || !payloadB) return [];
    return Array.from(new Set([...Object.keys(payloadA.metadata), ...Object.keys(payloadB.metadata)])).sort().map(key => {
      const valueA = payloadA.metadata[key];
      const valueB = payloadB.metadata[key];
      return { key, valueA, valueB, changed: stable(valueA) !== stable(valueB) };
    });
  }, [payloadA, payloadB]);

  const { rows: contentLineRows, hasDiff: hasContentDiff } = useMemo(
    () => payloadA && payloadB ? buildLineDiffRows(payloadA.content, effectiveContentB) : { rows: [] as LineDiffRow[], hasDiff: false },
    [payloadA, payloadB, effectiveContentB]
  );
  const contentDiffFlags = useMemo(() => ({
    hasInsertions: contentLineRows.some(r => r.type === 'insert'),
    hasDeletions: contentLineRows.some(r => r.type === 'delete'),
    hasModifications: contentLineRows.some(r => r.type === 'changed')
  }), [contentLineRows]);

  const diffHunks = useMemo(() => {
    const hunks: Array<{ start: number; end: number }> = [];
    let start: number | null = null;
    for (let i = 0; i < contentLineRows.length; i++) {
      const isChange = contentLineRows[i].type !== 'equal';
      if (isChange && start === null) start = i;
      if (!isChange && start !== null) {
        hunks.push({ start, end: i - 1 });
        start = null;
      }
    }
    if (start !== null) hunks.push({ start, end: contentLineRows.length - 1 });
    return hunks;
  }, [contentLineRows]);

  const hunkStartIndexToHunkNumber = useMemo(() => {
    const map = new Map<number, number>();
    diffHunks.forEach((h, i) => map.set(h.start, i));
    return map;
  }, [diffHunks]);

  useEffect(() => {
    setActiveHunkIndex(0);
    setSummaryOnlyChanged(false);
  }, [versionAId, versionBId]);

  useEffect(() => {
    setActiveVisualChangeIndex(0);
  }, [versionAId, versionBId, formattedDiff.previousHtml, formattedDiff.currentHtml]);

  useLayoutEffect(() => {
    if (!payloadB) {
      setEditedContentB('');
      return;
    }
    setEditedContentB(baseContentB);
  }, [baseContentB, versionBId, versionB?.payload]);

  useEffect(() => {
    if (!diffHunks.length) return;
    if (activeHunkIndex < 0) setActiveHunkIndex(0);
    else if (activeHunkIndex > diffHunks.length - 1) setActiveHunkIndex(diffHunks.length - 1);
  }, [activeHunkIndex, diffHunks.length]);

  const scrollToHunk = useCallback((hunkIndex: number) => {
    const clamped = Math.max(0, Math.min(hunkIndex, diffHunks.length - 1));
    setActiveHunkIndex(clamped);
    const el = document.getElementById(`diff-hunk-${clamped}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    lineDiffContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [diffHunks.length]);

  const activeHunk = diffHunks.length ? diffHunks[Math.max(0, Math.min(activeHunkIndex, diffHunks.length - 1))] : null;
  const isRowInActiveHunk = useCallback((rowIndex: number) => {
    if (!activeHunk) return false;
    return rowIndex >= activeHunk.start && rowIndex <= activeHunk.end;
  }, [activeHunk]);

  const computeBlocks = useCallback((scrollContainer: HTMLDivElement | null) => {
    if (!scrollContainer) return [] as Array<{ start: number; end: number }>;
    const editor = scrollContainer.querySelector('.ql-editor') as HTMLElement | null;
    if (!editor) return [] as Array<{ start: number; end: number }>;
    const nodes = Array.from(editor.querySelectorAll('.diff-ins, .diff-del')) as HTMLElement[];
    if (!nodes.length) return [] as Array<{ start: number; end: number }>;

    const editorRect = editor.getBoundingClientRect();
    const tops = nodes
      .map(n => {
        const r = n.getBoundingClientRect();
        return Math.max(0, Math.floor((r.top - editorRect.top) + scrollContainer.scrollTop));
      })
      .sort((a, b) => a - b);

    const blocks: Array<{ start: number; end: number }> = [];
    const thresholdPx = 120;
    let start = tops[0];
    let last = tops[0];
    for (let i = 1; i < tops.length; i++) {
      const t = tops[i];
      if (t - last > thresholdPx) {
        blocks.push({ start, end: last });
        start = t;
      }
      last = t;
    }
    blocks.push({ start, end: last });
    return blocks;
  }, []);

  const recomputeVisualChangeBlocks = useCallback(() => {
    const leftBlocks = computeBlocks(visualLeftContainerRef.current);
    const rightBlocks = computeBlocks(visualRightContainerRef.current);
    const count = Math.max(leftBlocks.length, rightBlocks.length);
    const merged: Array<{ leftTop: number; rightTop: number; leftEnd: number; rightEnd: number }> = [];
    for (let i = 0; i < count; i++) {
      const l = leftBlocks[i] ?? null;
      const r = rightBlocks[i] ?? null;
      if (!l && !r) continue;
      merged.push({
        leftTop: l?.start ?? 0,
        leftEnd: l?.end ?? l?.start ?? 0,
        rightTop: r?.start ?? 0,
        rightEnd: r?.end ?? r?.start ?? 0
      });
    }
    setVisualChangeBlocks(merged);
  }, [computeBlocks]);

  useEffect(() => {
    if (diffViewMode !== 'visual') return;
    // ReactQuill renderiza async; recalcular após layout estabilizar (editedContentB/sync keys após salvar)
    const run = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          recomputeVisualChangeBlocks();
        });
      });
    };
    const t1 = window.setTimeout(run, 0);
    const t2 = window.setTimeout(() => recomputeVisualChangeBlocks(), 80);
    const onResize = () => recomputeVisualChangeBlocks();
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', onResize);
    };
  }, [
    diffViewMode,
    recomputeVisualChangeBlocks,
    formattedDiff.previousHtml,
    formattedDiff.currentHtml,
    editedContentB,
    visualLeftQuillKey,
    visualRightQuillKey
  ]);

  const scrollToVisualChange = useCallback((idx: number) => {
    if (!visualChangeBlocks.length) return;
    const clamped = Math.max(0, Math.min(idx, visualChangeBlocks.length - 1));
    setActiveVisualChangeIndex(clamped);
    const block = visualChangeBlocks[clamped];
    visualLeftContainerRef.current?.scrollTo({ top: Math.max(0, block.leftTop - 40), behavior: 'smooth' });
    visualRightContainerRef.current?.scrollTo({ top: Math.max(0, block.rightTop - 40), behavior: 'smooth' });
  }, [visualChangeBlocks]);

  useEffect(() => {
    if (diffViewMode !== 'visual') return;
    const applyFocus = (scrollContainer: HTMLDivElement | null, range: { start: number; end: number }, side: 'left' | 'right') => {
      if (!scrollContainer) return;
      const editor = scrollContainer.querySelector('.ql-editor') as HTMLElement | null;
      if (!editor) return;
      const nodes = Array.from(editor.querySelectorAll('.diff-ins, .diff-del')) as HTMLElement[];
      const editorRect = editor.getBoundingClientRect();
      for (const n of nodes) {
        const r = n.getBoundingClientRect();
        const top = Math.max(0, Math.floor((r.top - editorRect.top) + scrollContainer.scrollTop));
        const inRange = top >= range.start && top <= range.end;
        if (inRange) {
          n.classList.add('diff-focus');
          n.setAttribute('data-diff-focus', side);
        } else {
          n.classList.remove('diff-focus');
          n.removeAttribute('data-diff-focus');
        }
      }
    };

    if (!visualChangeBlocks.length) return;
    const block = visualChangeBlocks[Math.max(0, Math.min(activeVisualChangeIndex, visualChangeBlocks.length - 1))];
    applyFocus(visualLeftContainerRef.current, { start: block.leftTop, end: block.leftEnd }, 'left');
    applyFocus(visualRightContainerRef.current, { start: block.rightTop, end: block.rightEnd }, 'right');
  }, [activeVisualChangeIndex, diffViewMode, visualChangeBlocks]);

  const loadData = useCallback(async (): Promise<{ history: DocumentVersion[]; doc: DocumentDto } | null> => {
    if (!documentId) return null;
    setLoading(true);
    try {
      const doc = await documentService.getDocument(documentId);
      if (!doc || doc.nodeType !== 'edoc') {
        addToast(
          'O histórico e a comparação de versões estão disponíveis apenas para documentos eletrônicos criados no sistema.',
          'error'
        );
        navigate('/documents', { replace: true });
        return null;
      }

      setDocumentCoverUrl(null);
      try {
        const details = await documentService.getDocumentDetails(documentId);
        const cu = details?.cover?.url?.trim();
        setDocumentCoverUrl(cu || null);
      } catch {
        setDocumentCoverUrl(null);
      }

      const history = await documentService.getDocumentVersionHistory(documentId);
      const list = history || [];
      setVersions(list);
      setDocumentTitle(doc?.title || '');
      setDocumentDescription(doc?.description || '');

      try
      {
        const [activeSig, latestSig, workflow] = await Promise.all([
          documentSignatureService.getActiveByDocument(documentId),
          documentSignatureService.getLatestByDocument(documentId),
          documentWorkflowService.getDocumentWorkflow(documentId),
        ]);
        const candidates = [activeSig, latestSig].filter(Boolean);
        setHasBlockingSignature(
          candidates.some(
            (sig) =>
              !!sig
              && (isInProgressSignatureRequestStatus(sig.status)
                || isCompletedSignatureRequestStatus(sig.status))
          )
        );
        setDocumentWorkflow(workflow);
      }
      catch
      {
        setHasBlockingSignature(false);
        setDocumentWorkflow(null);
      }

      const paramV1 = searchParams.get('v1');
      const paramV2 = searchParams.get('v2');

      if (list.length > 0) {
        const current = list.find(v => v.isCurrent);
        const previous = list.find(v => !v.isCurrent) || list[1];

        const initialA = paramV1 && list.some(v => v.id === paramV1) ? paramV1 : previous?.id || list[0].id;
        const initialB = paramV2 && list.some(v => v.id === paramV2) ? paramV2 : current?.id || list[0].id;

        setVersionAId(initialA || null);
        setVersionBId(initialB || null);

        const nextParams: Record<string, string> = {};
        if (initialA) nextParams.v1 = initialA;
        if (initialB) nextParams.v2 = initialB;
        setSearchParams(nextParams, { replace: true });
      }

      return { history: list, doc };
    } catch {
      addToast('Erro ao carregar versões para comparação', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [addToast, documentId, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChangeVersionA = (id: string) => {
    setVersionAId(id || null);
    const next: Record<string, string> = {};
    if (id) next.v1 = id;
    if (versionBId) next.v2 = versionBId;
    setSearchParams(next, { replace: true });
  };

  const handleChangeVersionB = (id: string) => {
    setVersionBId(id || null);
    const next: Record<string, string> = {};
    if (versionAId) next.v1 = versionAId;
    if (id) next.v2 = id;
    setSearchParams(next, { replace: true });
  };

  const renderAttachments = (list: ParsedAttachment[]) => list.length ? (
    <ul className="space-y-1">
      {list.map((a, i) => (
        <li key={`${a.name}-${i}`} className="text-sm text-gray-800 break-all">
          {a.url ? (
            <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              {a.name}
            </a>
          ) : a.name}
        </li>
      ))}
    </ul>
  ) : <span className="text-sm text-gray-500">Nenhum arquivo</span>;

  const renderCover = (resolvedUrl: string) => {
    const url = resolvedUrl?.trim();
    if (!url) {
      return <span className="text-sm text-gray-500">Não informado</span>;
    }
    return (
      <button
        type="button"
        onClick={() => setCoverModalUrl(url)}
        className="text-sm text-blue-600 hover:underline break-all"
      >
        Visualizar imagem
      </button>
    );
  };

  const renderContentHtml = (html: string, quillKey: string) => (
    <div className="quill-readonly-content">
      <ReactQuill key={quillKey} value={html} readOnly theme="snow" modules={readOnlyModules} />
    </div>
  );

  const renderEditableContentB = () => (
    <div className="quill-editable-content">
      <ReactQuill
        value={editedContentB}
        onChange={(value) => setEditedContentB(value)}
        theme="snow"
        modules={editModules}
        formats={editFormats}
      />
    </div>
  );

  const handleSaveB = async () => {
    if (!documentId || !hasPendingChangesB)
    {
      return;
    }
    if (!canEditB)
    {
      addToast('Edição não permitida para este documento no estado atual.', 'error');
      return;
    }
    setSavingB(true);
    try {
      const attachmentsForUpdate = (payloadB?.attachments || [])
        .filter(attachment => Boolean(attachment.url))
        .map((attachment, index) => ({
          id: `version-attachment-${index}`,
          name: attachment.name,
          size: 0,
          type: '',
          url: attachment.url || '',
          uploadedAt: new Date(),
          uploadedBy: ''
        }));

      const updateData: Partial<Document> = {
        title: payloadB?.title || documentTitle,
        description: payloadB?.description ?? documentDescription,
        content: editedContentB,
        coverImage: payloadB?.coverImage || undefined,
        updatedAt: new Date().toISOString()
      };

      if (attachmentsForUpdate.length > 0) {
        updateData.attachments = attachmentsForUpdate;
      }

      await documentService.updateDocument(documentId, updateData);
      addToast('Versão atual atualizada com sucesso', 'success');
      const fresh = await loadData();
      if (fresh?.history && versionBId) {
        const vb = fresh.history.find(v => v.id === versionBId);
        if (vb) {
          const pb = parsePayloadWithFallback(vb.payload, {
            title: fresh.doc.title || '',
            status: vb.status,
            updatedAt: vb.createdAt,
            description: vb.isCurrent ? (fresh.doc.description || '') : undefined
          });
          setEditedContentB(toHtml(pb.content));
        }
      }
    } catch {
      addToast('Erro ao atualizar a versão atual', 'error');
    } finally {
      setSavingB(false);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/documents');
    }
  };

  return (
    <div className="flex h-full flex-col">
      <style>{`
        .quill-readonly-content .ql-toolbar.ql-snow{display:none}
        .quill-readonly-content .ql-container.ql-snow{border:0;font-size:.875rem}
        .quill-readonly-content .ql-editor{padding:0;min-height:80px;white-space:pre-wrap;word-break:break-word}
        .quill-readonly-content .diff-del{background:#fef2f2;border:1px solid #fca5a5;border-radius:4px;text-decoration:line-through;padding:0 2px}
        .quill-readonly-content .diff-ins{background:#ecfdf5;border:1px solid #86efac;border-radius:4px;padding:0 2px}
        .quill-readonly-content .diff-focus{box-shadow:0 0 0 2px rgba(59,130,246,.35);border-color:rgba(59,130,246,.55)!important}
      `}</style>

      <header className="mb-4 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <button
                type="button"
                onClick={handleGoBack}
                className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                Voltar
              </button>
              <span>/</span>
              <span>Documentos</span>
              <span>/</span>
              <span className="font-medium text-gray-700">Comparar versões</span>
            </div>
            <h1 className="mt-2 text-xl font-semibold text-gray-900">
              {documentTitle || 'Documento'}
            </h1>
          </div>
          <div className="flex flex-col items-end gap-2 text-xs text-gray-500">
            <span>Comparação detalhada de conteúdo e metadados</span>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Comparar:</label>
            <select
              value={versionAId || ''}
              onChange={e => handleChangeVersionA(e.target.value || '')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Selecione</option>
              {versions.map(v => (
                <option key={v.id} value={v.id}>
                  v{v.versionLabel} {v.isCurrent ? '(Selecionada)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Com:</label>
            <select
              value={versionBId || ''}
              onChange={e => handleChangeVersionB(e.target.value || '')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Selecione</option>
              {versions.map(v => (
                <option key={v.id} value={v.id}>
                  v{v.versionLabel} {v.isCurrent ? '(Atual)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end justify-end">
            {versionB && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium border ${statusClassName(versionB.status)}`}>
                {statusLabel(versionB.status)}
              </span>
            )}
          </div>
        </div>

        {loading && (
          <div className="mt-2 text-xs text-gray-500">
            Carregando versões...
          </div>
        )}
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto px-6 pb-6">
        {!versionA || !versionB || !payloadA || !payloadB ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
            Selecione duas versões para visualizar a comparação detalhada.
          </div>
        ) : (
          <>
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm font-medium text-gray-700">Modo de visualizacao do diff</div>
                <div className="flex items-center gap-4 text-sm text-gray-700">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="diff-view-mode"
                      value="visual"
                      checked={diffViewMode === 'visual'}
                      onChange={() => setDiffViewMode('visual')}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Visual</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="diff-view-mode"
                      value="line"
                      checked={diffViewMode === 'line'}
                      onChange={() => setDiffViewMode('line')}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Linha a linha</span>
                  </label>
                </div>
              </div>
            </section>

            {diffViewMode === 'line' && hasContentDiff && (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-700">
                      Diff linha a linha (texto plano)
                    </div>
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 border border-blue-200">
                      Prioritário
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500">
                      {diffHunks.length ? (
                        <span>
                          Alteração <span className="font-medium text-gray-700">{activeHunkIndex + 1}</span> de{' '}
                          <span className="font-medium text-gray-700">{diffHunks.length}</span>
                        </span>
                      ) : (
                        <span>Sem alterações detectadas</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => scrollToHunk(activeHunkIndex - 1)}
                      disabled={!diffHunks.length || activeHunkIndex <= 0}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      ← Anterior
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollToHunk(activeHunkIndex + 1)}
                      disabled={!diffHunks.length || activeHunkIndex >= diffHunks.length - 1}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Próxima →
                    </button>
                  </div>
                </div>

                <div ref={lineDiffContainerRef} className="max-h-[50vh] overflow-y-auto rounded-md border border-gray-200">
                  <table className="min-w-full table-fixed text-xs">
                    <colgroup>
                      <col className="w-12" />
                      <col className="w-1/2" />
                      <col className="w-12" />
                      <col className="w-1/2" />
                    </colgroup>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-r border-gray-200 px-2 py-1 text-right font-mono font-normal text-gray-400">
                          #
                        </th>
                        <th className="border-b border-r border-gray-200 px-2 py-1 text-left font-medium text-gray-600">
                        v{versionA.versionLabel}
                        </th>
                        <th className="border-b border-r border-gray-200 px-2 py-1 text-right font-mono font-normal text-gray-400">
                          #
                        </th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left font-medium text-gray-600">
                        v{versionB.versionLabel}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {contentLineRows.map((row, idx) => (
                        <tr
                          key={idx}
                          id={hunkStartIndexToHunkNumber.has(idx) ? `diff-hunk-${hunkStartIndexToHunkNumber.get(idx)}` : undefined}
                          className={`align-top ${isRowInActiveHunk(idx) ? 'outline outline-2 outline-slate-200 outline-offset-[-2px]' : ''}`}
                        >
                          <td className={`border-b border-r border-gray-200 px-2 py-1 text-right font-mono text-[11px] text-gray-400 ${row.type !== 'equal' ? 'bg-gray-50' : ''}`}>
                            {row.leftNumber ?? ''}
                          </td>
                          <td className={`border-b border-r border-gray-200 px-2 py-1 font-mono text-[11px] text-gray-800 ${lineBlockClass(row.type, 'left')}`}>
                            {row.leftText}
                          </td>
                          <td className={`border-b border-r border-gray-200 px-2 py-1 text-right font-mono text-[11px] text-gray-400 ${row.type !== 'equal' ? 'bg-gray-50' : ''}`}>
                            {row.rightNumber ?? ''}
                          </td>
                          <td className={`border-b border-gray-200 px-2 py-1 font-mono text-[11px] text-gray-800 ${lineBlockClass(row.type, 'right')}`}>
                            {row.rightText}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {diffHunks.length > 1 && (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-xs text-gray-600">
                      Dica: use <span className="font-medium">Anterior/Próxima</span> para navegar entre blocos de alteração.
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => scrollToHunk(activeHunkIndex - 1)}
                        disabled={activeHunkIndex <= 0}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        ← Anterior
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollToHunk(activeHunkIndex + 1)}
                        disabled={activeHunkIndex >= diffHunks.length - 1}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próxima →
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {diffViewMode === 'line' && !hasContentDiff && (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-sm font-medium text-gray-700">Diff linha a linha (texto plano)</div>
                <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  Sem alteracoes detectadas no diff linha a linha.
                </div>
              </section>
            )}

            {diffViewMode === 'visual' && (
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-gray-700">
                  Comparação de conteúdo: v{versionA.versionLabel} × v{versionB.versionLabel}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold border ${
                      hasContentDiff ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-green-100 text-green-800 border-green-200'
                    }`}
                  >
                    {hasContentDiff ? 'Com diferenças' : 'Sem diferenças'}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold border ${
                      contentDiffFlags.hasInsertions ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-600 border-gray-200'
                    }`}
                  >
                    + Adição
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold border ${
                      contentDiffFlags.hasDeletions ? 'bg-red-100 text-red-800 border-red-300' : 'bg-gray-100 text-gray-600 border-gray-200'
                    }`}
                  >
                    - Remoção
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 font-semibold border ${
                      contentDiffFlags.hasModifications ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-gray-100 text-gray-600 border-gray-200'
                    }`}
                  >
                    ✎ Modificação
                  </span>
                </div>
              </div>

              {formattedDiff.hasDiff && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-600">
                    {visualChangeBlocks.length ? (
                      <span>
                        Alteração <span className="font-medium text-gray-800">{activeVisualChangeIndex + 1}</span> de{' '}
                        <span className="font-medium text-gray-800">{visualChangeBlocks.length}</span> (diff visual)
                      </span>
                    ) : (
                      <span>Diferenças marcadas no conteúdo</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => scrollToVisualChange(activeVisualChangeIndex - 1)}
                      disabled={!visualChangeBlocks.length || activeVisualChangeIndex <= 0}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      ← Anterior
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollToVisualChange(activeVisualChangeIndex + 1)}
                      disabled={!visualChangeBlocks.length || activeVisualChangeIndex >= visualChangeBlocks.length - 1}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Próxima →
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-md border-l-4 border-l-red-300 border border-red-100 bg-white">
                  <div className="border-b border-gray-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-900">
                    v{versionA.versionLabel}
                  </div>
                  <div ref={visualLeftContainerRef} className="overflow-y-auto p-3">
                    {renderContentHtml(formattedDiff.previousHtml, visualLeftQuillKey)}
                  </div>
                </div>
                <div className="rounded-md border-l-4 border-l-green-400 border border-green-200 bg-green-50">
                  <div className="border-b border-green-200 bg-green-100 px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-medium text-green-900">
                        v{versionB.versionLabel}
                      </div>
                      {canEditB && hasPendingChangesB && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEditedContentB(baseContentB)}
                            disabled={savingB}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveB}
                            disabled={savingB}
                            className="rounded-md border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            {savingB ? 'Salvando...' : 'Salvar'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    ref={visualRightContainerRef}
                    className={`overflow-y-auto bg-white p-3 ${canEditB ? '' : 'max-h-[60vh]'}`}
                  >
                    {canEditB ? renderEditableContentB() : renderContentHtml(formattedDiff.currentHtml, visualRightQuillKey)}
                  </div>
                </div>
              </div>
            </section>
            )}

            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-medium text-gray-700">Campos principais</div>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${
                      summaryRows.some(r => r.changed)
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                        : 'bg-green-100 text-green-800 border-green-200'
                    }`}
                  >
                    {summaryRows.some(r => r.changed) ? 'Com alterações' : 'Sem alterações'}
                  </span>
                </div>
                <div
                  className="inline-flex rounded-md border border-gray-300 bg-gray-100 p-0.5 text-xs font-medium"
                  role="group"
                  aria-label="Filtrar campos principais"
                >
                  <button
                    type="button"
                    onClick={() => setSummaryOnlyChanged(false)}
                    className={`rounded px-2.5 py-1 transition-colors ${
                      !summaryOnlyChanged
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    onClick={() => setSummaryOnlyChanged(true)}
                    disabled={summaryChangedCount === 0}
                    className={`rounded px-2.5 py-1 transition-colors ${
                      summaryOnlyChanged
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    Só alterados
                    {summaryChangedCount > 0 ? ` (${summaryChangedCount})` : ''}
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                {displayedSummaryRows.length === 0 ? (
                  <div className="border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm text-gray-600">
                    {summaryOnlyChanged
                      ? 'Nenhum campo principal alterado entre estas versões.'
                      : 'Nenhum campo a exibir.'}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 border-b border-gray-200 bg-gray-50 md:grid-cols-2">
                      <div className="border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 md:border-b-0 md:border-r md:border-gray-200">
                        v{versionA.versionLabel}
                      </div>
                      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-green-800">
                        v{versionB.versionLabel}
                      </div>
                    </div>
                    {displayedSummaryRows.map((row, idx) => (
                      <div
                        key={row.key}
                        className={`grid grid-cols-1 md:grid-cols-2 ${
                          idx < displayedSummaryRows.length - 1 ? 'border-b border-gray-100' : ''
                        } ${row.changed ? 'bg-amber-50/35' : 'bg-white'}`}
                      >
                        <div
                          className={`border-b border-gray-100 px-3 py-2.5 md:border-b-0 md:border-r md:border-gray-100 ${
                            row.changed ? 'md:bg-red-50/25' : ''
                          }`}
                        >
                          <div className="text-xs font-medium text-gray-500">{row.label}</div>
                          <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-900">
                            {row.key === 'coverImage'
                              ? renderCover(String(row.a))
                              : row.key === 'attachments'
                                ? renderAttachments(row.a as ParsedAttachment[])
                                : String(row.a)}
                          </div>
                        </div>
                        <div className={`px-3 py-2.5 ${row.changed ? 'md:bg-green-50/30' : ''}`}>
                          <div className="flex flex-wrap items-center justify-between gap-1">
                            <div className="text-xs font-medium text-gray-500">{row.label}</div>
                            {row.changed && (
                              <span className="shrink-0 text-[11px] font-medium text-amber-800">Alterado</span>
                            )}
                          </div>
                          <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-900">
                            {row.key === 'coverImage'
                              ? renderCover(String(row.b))
                              : row.key === 'attachments'
                                ? renderAttachments(row.b as ParsedAttachment[])
                                : String(row.b)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </section>

            {metadataRows.length > 0 && (
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3 text-sm font-medium text-gray-700">Metadados</div>
                <div className="max-h-[40vh] space-y-3 overflow-y-auto pr-1">
                  {metadataRows.map(row => (
                    <div key={row.key} className="rounded-md border border-gray-200">
                      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                          {row.key}
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                            row.changed
                              ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                              : 'bg-green-100 text-green-800 border-green-200'
                          }`}
                        >
                          {row.changed ? 'Alterado' : 'Igual'}
                        </span>
                      </div>
                      <div className="grid gap-3 px-3 py-3 md:grid-cols-2">
                        <div className="rounded border border-gray-200 bg-white p-2">
                          <div className="mb-1 text-xs text-gray-500">Versão anterior</div>
                          <div className="whitespace-pre-wrap break-words text-sm text-gray-800">
                            {formatUnknown(row.valueA)}
                          </div>
                        </div>
                        <div className="rounded border border-gray-200 bg-white p-2">
                          <div className="mb-1 text-xs text-gray-500">Versão selecionada</div>
                          <div className="whitespace-pre-wrap break-words text-sm text-gray-800">
                            {formatUnknown(row.valueB)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

          </>
        )}
      </div>

      <Transition appear show={coverModalUrl !== null} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setCoverModalUrl(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-4xl rounded-lg bg-white p-4 shadow-xl">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <Dialog.Title className="text-sm font-medium text-gray-900">Imagem de capa</Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setCoverModalUrl(null)}
                      className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      aria-label="Fechar"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {coverModalUrl ? (
                    <div className="flex max-h-[80vh] items-center justify-center overflow-auto rounded-md border border-gray-200 bg-gray-50">
                      <img
                        src={coverModalUrl}
                        alt="Capa do documento"
                        className="max-h-[75vh] max-w-full object-contain"
                        onError={() => {
                          addToast('Não foi possível carregar a imagem de capa.', 'error');
                          setCoverModalUrl(null);
                        }}
                      />
                    </div>
                  ) : null}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default DocumentVersionComparePage;

